import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LockKey, User as UserIcon, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

function formatErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Login() {
    const { user, loading, setUser } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!loading && user?.is_admin) {
            navigate("/admin", { replace: true });
        }
    }, [user, loading, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError("");
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/login", { username, password });
            setUser(data);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid place-items-center px-6 py-24" data-testid="login-page">
            <div className="w-full max-w-md border border-zinc-800 bg-[#121212] p-10">
                <div className="overline mb-4">// Restricted Access</div>
                <h1 className="font-display text-white text-5xl leading-[0.9] mb-6">
                    ADMIN<br /><span className="text-[#FFD000]">LOGIN.</span>
                </h1>
                <p className="text-zinc-400 mb-8 text-sm leading-relaxed">
                    Enter your admin credentials to manage articles, reviews, and videos on Tizona Tech.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                    <div>
                        <label htmlFor="username" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                            Username
                        </label>
                        <div className="relative">
                            <UserIcon size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <input
                                id="username"
                                type="text"
                                autoComplete="username"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck="false"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                data-testid="login-username-input"
                                className="w-full bg-[#0A0A0A] border border-zinc-800 text-white pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FFD000] transition-colors"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="password" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <LockKey size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="w-full bg-[#0A0A0A] border border-zinc-800 text-white pl-10 pr-12 py-3 text-sm focus:outline-none focus:border-[#FFD000] transition-colors"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                data-testid="login-toggle-password"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#FFD000] transition-colors"
                            >
                                {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="border border-red-900 bg-red-950/40 px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] text-red-400"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="login-submit-button"
                        className="w-full btn-primary inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {submitting ? "Signing in…" : "Sign In"}
                        <ArrowRight size={16} weight="bold" />
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-zinc-800 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    Tizona Tech CMS · Secure Login
                </div>
            </div>
        </div>
    );
}
